/**
 * JobSheetPage — personal task view ("My tasks only — always personal").
 *
 * Header row:  Title + subtitle
 * Stats row:   My Total · In Progress · Overdue · Done  (compact chips)
 * Filter row:  [N tasks] [Status ▼] [Priority ▼] [⊞ Columns ▼] [Archive] [+ Add]
 *
 * Active view (default): excludes done tasks.
 * Archive view: shows only my done tasks.
 *
 * Task creation is optimistic: AddTaskModal returns the inserted row
 * (joins included) and we upsert it into the list immediately — the new
 * task appears in well under a second, no refetch round trips.
 */

import { useMemo, useState } from 'react';
import AppShell from '../../components/layout/AppShell.jsx';
import TaskTable from '../../components/task/Tasktable.jsx';
import AddTaskModal from '../../components/task/Addtaskmodal.jsx';
import RemarkModal from '../../components/task/Remarkmodal.jsx';
import { useTasks } from '../../hooks/usetasks.js';
import { useAuth } from '../../context/AuthContext.jsx';
import Icon from '../../components/ui/Icons.jsx';
import StatCard from '../../components/ui/StatCard.jsx';
import ColumnsDropdown from '../../components/ui/ColumnsDropdown.jsx';
import { TASK_STATUS_LABELS } from '../../data/constants.js';
import { useColumnPrefs } from '../../hooks/useColumnPrefs.js';
import { canAddRemarks, canDeleteTask } from '../../utils/roleUtils.js';
import { htmlToText } from '../../utils/sanitizeHtml.js';
import { supabase } from '../../api/supabase.js';

const STATUS_OPTS_ACTIVE = [
  { value: 'all',         label: 'All statuses'                 },
  { value: 'todo',        label: TASK_STATUS_LABELS.todo        },
  { value: 'in_progress', label: TASK_STATUS_LABELS.in_progress },
  { value: 'overdue',     label: TASK_STATUS_LABELS.overdue     },
  { value: 'redo',        label: TASK_STATUS_LABELS.redo        },
];

const PRIORITY_OPTS = [
  { value: 'all',    label: 'All priorities' },
  { value: 'low',    label: 'Low'            },
  { value: 'medium', label: 'Medium'         },
  { value: 'high',   label: 'High'           },
];

/* ── Page ──────────────────────────────────────────────────── */
export default function JobSheetPage() {
  const { profile } = useAuth();
  const { tasks, loading, error, upsertTask, patchTask, removeTask } = useTasks();

  const [addOpen,        setAddOpen]        = useState(false);
  const [remarkTask,     setRemarkTask]     = useState(null);
  const [isArchive,      setIsArchive]      = useState(false);
  const [statusFilter,   setStatusFilter]   = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const colPrefs = useColumnPrefs('jobsheet', false);

  const handleViewSwitch = (archive) => {
    setIsArchive(archive);
    setStatusFilter('all');
  };

  /* TL+ row actions */
  const allowRemark = canAddRemarks(profile?.role);
  const allowDelete = canDeleteTask(profile?.role);

  const handleDelete = async (task) => {
    const title = htmlToText(task.title) || 'this task';
    if (!window.confirm(`Delete “${title}”? This cannot be undone.`)) return;
    removeTask(task.id);
    // .select() confirms a row was actually deleted — an RLS-filtered
    // no-op returns zero rows with no error, which would silently lie.
    const { data, error: deleteError } = await supabase
      .from('tasks')
      .delete()
      .eq('id', task.id)
      .select('id');
    if (deleteError || !data?.length) {
      if (deleteError) console.error('[ORIN] Failed to delete task:', deleteError.message);
      upsertTask(task); // restore the row — the delete didn't happen
    }
  };

  /* My tasks only */
  const myTasks = useMemo(() => {
    if (!profile) return [];
    return tasks.filter((t) => t.owner_id === profile.id);
  }, [tasks, profile]);

  const activeTasks   = useMemo(() => myTasks.filter((t) => t.status !== 'done'), [myTasks]);
  const archivedTasks = useMemo(() => myTasks.filter((t) => t.status === 'done'),  [myTasks]);
  const viewTasks     = isArchive ? archivedTasks : activeTasks;

  /* Stats — single pass */
  const stats = useMemo(() => {
    const s = { total: myTasks.length, inProgress: 0, overdue: 0, done: 0 };
    for (const t of myTasks) {
      if (t.status === 'in_progress') s.inProgress += 1;
      else if (t.status === 'overdue') s.overdue += 1;
      else if (t.status === 'done') s.done += 1;
    }
    return s;
  }, [myTasks]);

  const filteredCount = useMemo(() =>
    viewTasks.filter((t) => {
      const s = statusFilter   === 'all' || t.status   === statusFilter;
      const p = priorityFilter === 'all' || t.priority === priorityFilter;
      return s && p;
    }).length,
  [viewTasks, statusFilter, priorityFilter]);

  return (
    <AppShell title="Job Sheet" sectionLabel="Workspace">

      {/* ── Stats cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4">
        <StatCard label="My tasks"    count={stats.total}      accent="#64748B" />
        <StatCard label="In Progress" count={stats.inProgress} accent="#0891B2" />
        <StatCard label="Overdue"     count={stats.overdue}    accent="#DC2626" />
        <StatCard label="Done"        count={stats.done}       accent="#16A34A" />
      </div>

      {/* ── Filter row ── */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-xs font-semibold text-muted bg-canvas border border-border-base rounded-full px-3 py-1 whitespace-nowrap flex-shrink-0">
          {filteredCount} {filteredCount === 1 ? 'task' : 'tasks'}
        </span>

        <div className="flex items-center gap-2 flex-wrap ml-auto">
          {/* Status filter — active view only */}
          {!isArchive && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input !w-auto text-sm py-1.5 pr-8 cursor-pointer"
              aria-label="Filter by status"
            >
              {STATUS_OPTS_ACTIVE.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}

          {/* Priority filter */}
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="input !w-auto text-sm py-1.5 pr-8 cursor-pointer"
            aria-label="Filter by priority"
          >
            {PRIORITY_OPTS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          {/* Columns toggle */}
          <ColumnsDropdown
            allToggleable={colPrefs.allToggleable}
            visible={colPrefs.visible}
            onToggle={colPrefs.toggleCol}
          />

          {/* Archive toggle */}
          <button
            type="button"
            onClick={() => handleViewSwitch(!isArchive)}
            className={`btn text-sm gap-1.5 ${
              isArchive
                ? 'bg-green-600 text-white border-green-600 hover:bg-green-700'
                : 'border border-border-base bg-card hover:bg-slate-50 text-muted'
            }`}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/>
            </svg>
            {isArchive ? 'Back to active' : 'Completed Tasks'}
          </button>

          <button
            type="button"
            onClick={() => setAddOpen(true)}
            data-tour="add-task"
            className="btn-primary text-sm"
          >
            <Icon name="plus" size={15} />
            Add task
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div data-tour="task-table">
        <TaskTable
          tasks={viewTasks}
          loading={loading}
          error={error}
          columns={colPrefs.columns}
          widths={colPrefs.widths}
          onColumnResize={colPrefs.setWidth}
          statusFilter={statusFilter}
          priorityFilter={priorityFilter}
          onRemark={allowRemark ? setRemarkTask : undefined}
          onDelete={allowDelete ? handleDelete : undefined}
        />
      </div>

      <AddTaskModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(row) => { if (row) upsertTask(row); }}
      />

      <RemarkModal
        open={!!remarkTask}
        task={remarkTask}
        onClose={() => setRemarkTask(null)}
        onSaved={(id, value) => patchTask(id, { remarks: value })}
      />
    </AppShell>
  );
}
